// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Activity, Cpu, HardDrive, Database, Wifi, WifiOff, AlertTriangle, CheckCircle, AlertCircle, Info, RefreshCw, X, Database as Memory } from 'lucide-react';
import { apiFetch } from '../config/api';
import './PerformanceMonitor.css';

const PerformanceMonitor = ({ isOpen, onClose }) => {
  // Nouveaux états pour le monitoring avancé
  const [performanceMetrics, setPerformanceMetrics] = useState({
    memory: { used: 0, total: 0, percentage: 0 },
    cpu: { usage: 0, cores: 0 },
    disk: { used: 0, total: 0, percentage: 0 },
    network: { upload: 0, download: 0 },
    api: { responseTime: 0, errorRate: 0, requestsPerMinute: 0 }
  });
  const [alerts, setAlerts] = useState([]);
  const [showDetailedMetrics, setShowDetailedMetrics] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Seuils d'alerte
  const thresholds = {
    memory: 80, // %
    cpu: 70, // %
    disk: 85, // %
    apiResponseTime: 2000, // ms
    apiErrorRate: 5 // %
  };

  // Collecter les métriques de performance
  const collectMetrics = useCallback(async () => {
    try {
      // Récupérer les vraies métriques du backend avec gestion d'erreur robuste
      let systemResponse = {};
      let apiResponse = {};
      
      try {
        const systemRes = await apiFetch('admin/health');
        if (systemRes.ok) {
          systemResponse = await systemRes.json();
        }
      } catch (e) {
        console.warn('Erreur système:', e);
      }
      
      try {
        const apiRes = await apiFetch('analytics/metrics/system');
        if (apiRes.ok) {
          apiResponse = await apiRes.json();
        }
      } catch (e) {
        console.warn('Erreur API:', e);
      }
      
      // Métriques système réelles
      const systemMetrics = {
        memory_used: systemResponse.memory?.used || 0,
        memory_total: systemResponse.memory?.total || 1,
        memory_percent: systemResponse.memory?.percentage || 0,
        cpu_percent: systemResponse.cpu?.usage || 0,
        cpu_cores: systemResponse.cpu?.cores || navigator.hardwareConcurrency || 4,
        disk_used: systemResponse.disk?.used || 0,
        disk_total: systemResponse.disk?.total || 1,
        disk_percent: systemResponse.disk?.percentage || 0
      };
      
      // Métriques API réelles
      const apiMetrics = {
        avg_response_time: apiResponse.avg_response_time || 0,
        error_rate: apiResponse.error_rate || 0,
        requests_per_minute: apiResponse.requests_per_minute || 0
      };
      
      // Métriques réseau (basées sur les vraies données si disponibles)
      const networkMetrics = {
        upload: apiResponse.network?.upload || 0,
        download: apiResponse.network?.download || 0
      };

      const newMetrics = {
        memory: {
          used: systemMetrics.memory_used || 0,
          total: systemMetrics.memory_total || 0,
          percentage: systemMetrics.memory_percent || 0
        },
        cpu: {
          usage: systemMetrics.cpu_percent || 0,
          cores: systemMetrics.cpu_cores || 0
        },
        disk: {
          used: systemMetrics.disk_used || 0,
          total: systemMetrics.disk_total || 0,
          percentage: systemMetrics.disk_percent || 0
        },
        network: networkMetrics,
        api: {
          responseTime: apiMetrics.avg_response_time || 0,
          errorRate: apiMetrics.error_rate || 0,
          requestsPerMinute: apiMetrics.requests_per_minute || 0
        }
      };

      setPerformanceMetrics(newMetrics);
      checkAlerts(newMetrics);
    } catch (error) {
      console.error('Erreur collecte métriques:', error);
      // Retourner des métriques par défaut en cas d'erreur
      const defaultMetrics = {
        memory: { used: 0, total: 0, percentage: 0 },
        cpu: { usage: 0, cores: navigator.hardwareConcurrency || 4 },
        disk: { used: 0, total: 0, percentage: 0 },
        network: { upload: 0, download: 0 },
        api: { responseTime: 0, errorRate: 0, requestsPerMinute: 0 }
      };
      setPerformanceMetrics(defaultMetrics);
    }
  }, []);

  // Vérifier les alertes
  const checkAlerts = useCallback((metrics) => {
    const newAlerts = [];

    if (metrics.memory.percentage > thresholds.memory) {
      newAlerts.push({
        id: Date.now(),
        type: 'warning',
        message: `Utilisation mémoire élevée: ${metrics.memory.percentage.toFixed(1)}%`,
        metric: 'memory'
      });
    }

    if (metrics.cpu.usage > thresholds.cpu) {
      newAlerts.push({
        id: Date.now() + 1,
        type: 'warning',
        message: `Utilisation CPU élevée: ${metrics.cpu.usage.toFixed(1)}%`,
        metric: 'cpu'
      });
    }

    if (metrics.disk.percentage > thresholds.disk) {
      newAlerts.push({
        id: Date.now() + 2,
        type: 'error',
        message: `Espace disque critique: ${metrics.disk.percentage.toFixed(1)}%`,
        metric: 'disk'
      });
    }

    if (metrics.api.responseTime > thresholds.apiResponseTime) {
      newAlerts.push({
        id: Date.now() + 3,
        type: 'warning',
        message: `Temps de réponse API lent: ${metrics.api.responseTime.toFixed(0)}ms`,
        metric: 'api'
      });
    }

    if (metrics.api.errorRate > thresholds.apiErrorRate) {
      newAlerts.push({
        id: Date.now() + 4,
        type: 'error',
        message: `Taux d'erreur API élevé: ${metrics.api.errorRate.toFixed(1)}%`,
        metric: 'api'
      });
    }

    setAlerts(prev => [...newAlerts, ...prev.slice(0, 9)]); // Garder max 10 alertes
  }, []);

  // Auto-refresh des métriques
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(collectMetrics, 5000); // Toutes les 5 secondes
    return () => clearInterval(interval);
  }, [autoRefresh, collectMetrics]);

  // Collecter les métriques au montage
  useEffect(() => {
    collectMetrics();
  }, [collectMetrics]);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getMetricColor = (value, threshold, reverse = false) => {
    const percentage = (value / threshold) * 100;
    if (reverse) {
      if (percentage > 80) return '#ef4444';
      if (percentage > 60) return '#f59e0b';
      return '#10b981';
    } else {
      if (percentage > 80) return '#ef4444';
      if (percentage > 60) return '#f59e0b';
      return '#10b981';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'error':
        return <AlertCircle size={16} className="alert-icon error" />;
      case 'warning':
        return <AlertTriangle size={16} className="alert-icon warning" />;
      default:
        return <Info size={16} className="alert-icon info" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="performance-monitor-popup">
      <div className="popup-overlay" onClick={onClose}></div>
      <div className="popup-content">
        <div className="monitor-header">
          <h3>Monitoring Performance</h3>
          <div className="monitor-controls">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`refresh-btn ${autoRefresh ? 'active' : ''}`}
            >
              <RefreshCw size={16} />
              Auto-refresh
            </button>
            <button
              onClick={() => setShowDetailedMetrics(!showDetailedMetrics)}
              className="details-btn"
            >
              {showDetailedMetrics ? 'Vue simple' : 'Vue détaillée'}
            </button>
            <button
              onClick={onClose}
              className="btn-close"
              title="Fermer"
            >
              <X size={16} />
            </button>
          </div>
        </div>

      {/* Métriques principales */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-header">
            <Cpu size={20} />
            <span>CPU</span>
          </div>
          <div className="metric-value">
            {performanceMetrics.cpu.usage.toFixed(1)}%
          </div>
          <div className="metric-bar">
            <div 
              className="metric-fill"
              style={{ 
                width: `${performanceMetrics.cpu.usage}%`,
                backgroundColor: getMetricColor(performanceMetrics.cpu.usage, thresholds.cpu)
              }}
            />
          </div>
          <div className="metric-details">
            {performanceMetrics.cpu.cores} cœurs
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <Memory size={20} />
            <span>Mémoire</span>
          </div>
          <div className="metric-value">
            {performanceMetrics.memory.percentage.toFixed(1)}%
          </div>
          <div className="metric-bar">
            <div 
              className="metric-fill"
              style={{ 
                width: `${performanceMetrics.memory.percentage}%`,
                backgroundColor: getMetricColor(performanceMetrics.memory.percentage, thresholds.memory)
              }}
            />
          </div>
          <div className="metric-details">
            {formatBytes(performanceMetrics.memory.used)} / {formatBytes(performanceMetrics.memory.total)}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <HardDrive size={20} />
            <span>Disque</span>
          </div>
          <div className="metric-value">
            {performanceMetrics.disk.percentage.toFixed(1)}%
          </div>
          <div className="metric-bar">
            <div 
              className="metric-fill"
              style={{ 
                width: `${performanceMetrics.disk.percentage}%`,
                backgroundColor: getMetricColor(performanceMetrics.disk.percentage, thresholds.disk)
              }}
            />
          </div>
          <div className="metric-details">
            {formatBytes(performanceMetrics.disk.used)} / {formatBytes(performanceMetrics.disk.total)}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <Activity size={20} />
            <span>API</span>
          </div>
          <div className="metric-value">
            {performanceMetrics.api.responseTime.toFixed(0)}ms
          </div>
          <div className="metric-bar">
            <div 
              className="metric-fill"
              style={{ 
                width: `${Math.min((performanceMetrics.api.responseTime / thresholds.apiResponseTime) * 100, 100)}%`,
                backgroundColor: getMetricColor(performanceMetrics.api.responseTime, thresholds.apiResponseTime, true)
              }}
            />
          </div>
          <div className="metric-details">
            {performanceMetrics.api.requestsPerMinute.toFixed(0)} req/min
          </div>
        </div>
      </div>

      {/* Métriques détaillées */}
      {showDetailedMetrics && (
        <div className="detailed-metrics">
          <h4>Métriques détaillées</h4>
          
          <div className="detailed-grid">
            <div className="detail-card">
              <h5>Réseau</h5>
              <div className="detail-item">
                <span>Upload:</span>
                <span>{formatBytes(performanceMetrics.network.upload)}/s</span>
              </div>
              <div className="detail-item">
                <span>Download:</span>
                <span>{formatBytes(performanceMetrics.network.download)}/s</span>
              </div>
            </div>

            <div className="detail-card">
              <h5>API Performance</h5>
              <div className="detail-item">
                <span>Temps de réponse:</span>
                <span>{performanceMetrics.api.responseTime.toFixed(0)}ms</span>
              </div>
              <div className="detail-item">
                <span>Taux d'erreur:</span>
                <span>{performanceMetrics.api.errorRate.toFixed(1)}%</span>
              </div>
              <div className="detail-item">
                <span>Requêtes/min:</span>
                <span>{performanceMetrics.api.requestsPerMinute.toFixed(0)}</span>
              </div>
            </div>

            <div className="detail-card">
              <h5>Système</h5>
              <div className="detail-item">
                <span>CPU Cœurs:</span>
                <span>{performanceMetrics.cpu.cores}</span>
              </div>
              <div className="detail-item">
                <span>Mémoire totale:</span>
                <span>{formatBytes(performanceMetrics.memory.total)}</span>
              </div>
              <div className="detail-item">
                <span>Espace disque:</span>
                <span>{formatBytes(performanceMetrics.disk.total)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

             {/* Alertes */}
       {alerts.length > 0 && (
         <div className="alerts-section">
           <h4>Alertes ({alerts.length})</h4>
           <div className="alerts-list">
             {alerts.map(alert => (
               <div key={alert.id} className={`alert-item ${alert.type}`}>
                 {getAlertIcon(alert.type)}
                 <span className="alert-message">{alert.message}</span>
                 <button 
                   onClick={() => setAlerts(prev => prev.filter(a => a.id !== alert.id))}
                   className="alert-close"
                 >
                   <X size={16} />
                 </button>
               </div>
             ))}
           </div>
         </div>
       )}
      </div>
    </div>
  );
};

export default PerformanceMonitor;
